import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography,
} from '@mui/material';

import AIManagerService, {
    BillingSummary,
    clearDialogPackageCheckout,
    DialogPackage,
    DialogPackagePurchaseResponse,
    dialogPackageRequestKey,
    readDialogPackageCheckout,
    rememberDialogPackageCheckout,
    StoredDialogPackageCheckout,
    trustedPaymentUrl,
} from 'src/services/AIManagerService';

type Props = {
    open: boolean;
    product: 'tghunter' | 'leadsmania';
    balance: number;
    onClose: () => void;
    onBillingChange: (billing: BillingSummary) => void;
};

type CheckoutNotice = {
    severity: 'success' | 'info' | 'warning' | 'error';
    text: string;
};

const rubles = (value: string) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    return `${new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount)} ₽`;
};

const quote = (item: DialogPackage) => ({
    total: item.total_price,
    effective: item.effective_price_per_dialog,
});

const paymentReturned = () =>
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('payment') === 'pending' ||
        new URLSearchParams(window.location.search).get('payment_success') === 'true');

const returnedPurchase = (summary: BillingSummary) => {
    if (!paymentReturned()) return null;
    const returnedId = Number(new URLSearchParams(window.location.search).get('purchase_id'));
    if (!Number.isInteger(returnedId) || returnedId <= 0) return null;
    return (summary.recent_purchases || []).find((item) => item.id === returnedId) || null;
};

const checkoutFromHistory = (
    product: 'tghunter' | 'leadsmania',
    accountScope: string,
    summary: BillingSummary
): StoredDialogPackageCheckout | null => {
    if (!paymentReturned()) return null;
    const purchase = returnedPurchase(summary);
    if (!purchase?.request_key) return null;
    const restored: StoredDialogPackageCheckout = {
        requestKey: purchase.request_key,
        packageId: purchase.package_id,
        purchaseId: purchase.id,
        status: purchase.status,
    };
    try {
        rememberDialogPackageCheckout(product, accountScope, restored);
    } catch {
        // The exact purchase can still be restored from server history on return.
    }
    return restored;
};

export default function DialogPackageCheckout({ open, product, balance, onClose, onBillingChange }: Props) {
    const [packages, setPackages] = useState<DialogPackage[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [working, setWorking] = useState(false);
    const [notice, setNotice] = useState<CheckoutNotice | null>(null);
    const [accountScope, setAccountScope] = useState('');
    const [pendingCheckout, setPendingCheckout] = useState<StoredDialogPackageCheckout | null>(null);
    const actionLock = useRef(false);
    const reconciledKey = useRef<string | null>(null);

    const refreshBilling = async (response?: DialogPackagePurchaseResponse) => {
        if (response?.balance) {
            onBillingChange(response.balance);
            return;
        }
        onBillingChange(await AIManagerService.getBilling());
    };

    const saveResponse = (response: DialogPackagePurchaseResponse, storageScope: string) => {
        const status = response.purchase.status;
        const confirmationUrl = trustedPaymentUrl(response.confirmation_url);
        const saved: StoredDialogPackageCheckout = {
            requestKey: response.request_key,
            packageId: response.purchase.package_id,
            purchaseId: response.purchase.id,
            confirmationUrl,
            status,
        };
        try {
            rememberDialogPackageCheckout(product, storageScope, saved);
        } catch {
            // The return URL carries the purchase id; server history remains authoritative.
        }
        setPendingCheckout(saved);
        return { status, confirmationUrl };
    };

    const applyResponse = async (response: DialogPackagePurchaseResponse, redirect: boolean, storageScope: string) => {
        const paid = response.purchase.status === 'paid';
        if (paid !== response.paid_immediately) {
            saveResponse(
                {
                    ...response,
                    paid_immediately: false,
                    confirmation_url: '',
                    purchase: { ...response.purchase, status: 'pending' },
                },
                storageScope
            );
            setNotice({
                severity: 'error',
                text: 'Статус покупки требует повторной проверки.',
            });
            return;
        }
        const { status, confirmationUrl } = saveResponse(response, storageScope);
        if (status === 'paid') {
            await refreshBilling(response);
            clearDialogPackageCheckout(product, storageScope);
            setPendingCheckout(null);
            setNotice({ severity: 'success', text: 'Диалоги зачислены.' });
            return;
        }
        if (status === 'canceled') {
            setNotice({
                severity: 'warning',
                text: 'Оплата отменена. Можно начать новую покупку.',
            });
            return;
        }
        setNotice({
            severity: 'info',
            text: 'Покупка сохранена. Ждём подтверждение оплаты.',
        });
        if (redirect && confirmationUrl) window.location.assign(confirmationUrl);
        if (redirect && response.confirmation_url && !confirmationUrl) {
            setNotice({
                severity: 'error',
                text: 'Покупка сохранена, но ссылка оплаты не прошла проверку. Повторите попытку.',
            });
        }
    };

    const reconcile = async (checkout: StoredDialogPackageCheckout, storageScope = accountScope) => {
        if (!checkout.purchaseId || actionLock.current) return;
        actionLock.current = true;
        setWorking(true);
        try {
            const response = await AIManagerService.reconcileDialogPackagePurchase(
                checkout.purchaseId,
                checkout.requestKey
            );
            await applyResponse(response, false, storageScope);
        } catch {
            setNotice({
                severity: 'info',
                text: 'Статус оплаты пока неизвестен. Покупка сохранена, повторите проверку.',
            });
        } finally {
            actionLock.current = false;
            setWorking(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        setNotice(null);
        Promise.all([AIManagerService.listDialogPackages(), AIManagerService.getBilling()])
            .then(([items, summary]) => {
                if (!active) return;
                setPackages(items.filter((item) => item.is_active));
                onBillingChange(summary);
                setAccountScope(summary.billing_account_scope);
                if (!summary.billing_account_scope) {
                    setNotice({
                        severity: 'error',
                        text: 'Не удалось определить платёжный аккаунт.',
                    });
                    return;
                }
                const historyCheckout = checkoutFromHistory(product, summary.billing_account_scope, summary);
                const storedCheckout = readDialogPackageCheckout(product, summary.billing_account_scope);
                const recentPending = (summary.recent_purchases || []).find(
                    (item) => item.status === 'pending' && item.request_key
                );
                const recentCheckout = recentPending?.request_key
                    ? {
                          requestKey: recentPending.request_key,
                          packageId: recentPending.package_id,
                          purchaseId: recentPending.id,
                          status: 'pending' as const,
                      }
                    : null;
                if (!storedCheckout && recentCheckout) {
                    try {
                        rememberDialogPackageCheckout(product, summary.billing_account_scope, recentCheckout);
                    } catch {
                        // The pending purchase remains visible from the server summary.
                    }
                }
                const pending = historyCheckout || storedCheckout || recentCheckout;
                setPendingCheckout(pending);
                if (pending && ['draft', 'pending'].includes(pending.status)) {
                    setSelectedPackageId(pending.packageId);
                } else {
                    setSelectedPackageId(null);
                }
                if (!paymentReturned()) return;
                const serverReturn = returnedPurchase(summary);
                if (serverReturn?.status === 'paid') {
                    clearDialogPackageCheckout(product, summary.billing_account_scope);
                    setPendingCheckout(null);
                    setNotice({ severity: 'success', text: 'Диалоги зачислены.' });
                    return;
                }
                if (serverReturn?.status === 'canceled') {
                    setNotice({
                        severity: 'warning',
                        text: 'Оплата отменена. Можно начать новую покупку.',
                    });
                    return;
                }
                if (!pending?.purchaseId) {
                    setNotice({
                        severity: 'info',
                        text: 'Сохранённая покупка не найдена. Баланс обновлён без отметки об успехе.',
                    });
                    return;
                }
                const returnedId = Number(new URLSearchParams(window.location.search).get('purchase_id'));
                if (pending.purchaseId !== returnedId) {
                    setNotice({
                        severity: 'info',
                        text: 'Не удалось сопоставить возврат с сохранённой покупкой.',
                    });
                    return;
                }
                if (reconciledKey.current !== pending.requestKey) {
                    reconciledKey.current = pending.requestKey;
                    void reconcile(pending, summary.billing_account_scope);
                }
            })
            .catch(() => {
                if (active) {
                    setNotice({
                        severity: 'error',
                        text: 'Не удалось загрузить пакеты. Повторите позже.',
                    });
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
        // Re-open is the explicit refresh boundary for packages and billing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, product]);

    const purchase = async () => {
        if (!selectedPackageId || actionLock.current) return;
        const selected = packages.find((item) => item.id === selectedPackageId);
        if (!selected || !quote(selected).effective) {
            setNotice({
                severity: 'error',
                text: 'Сервер не вернул точную цену за диалог.',
            });
            return;
        }
        actionLock.current = true;
        setWorking(true);
        setNotice(null);
        try {
            const requestKey = dialogPackageRequestKey(product, accountScope, selectedPackageId);
            const response = await AIManagerService.purchaseDialogPackage(selectedPackageId, requestKey);
            await applyResponse(response, true, accountScope);
        } catch {
            setNotice({
                severity: 'info',
                text: 'Результат оплаты пока неизвестен. Операция сохранена, повторите попытку.',
            });
        } finally {
            actionLock.current = false;
            setWorking(false);
        }
    };

    const pending = pendingCheckout || readDialogPackageCheckout(product, accountScope);
    const startNewPurchase = () => {
        clearDialogPackageCheckout(product, accountScope);
        setPendingCheckout(null);
        setSelectedPackageId(null);
        setNotice(null);
    };

    return (
        <Dialog
            open={open}
            onClose={working ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="dialog-package-title"
        >
            <DialogTitle id="dialog-package-title">Пополнить диалоги</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography color="secondary.main">Доступно: {balance} диалогов</Typography>
                    {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
                    {loading ? (
                        <Box py={4} textAlign="center">
                            <CircularProgress size={28} />
                        </Box>
                    ) : packages.length ? (
                        <Stack spacing={1}>
                            {packages.map((item) => {
                                const price = quote(item);
                                const selected = item.id === selectedPackageId;
                                return (
                                    <Paper
                                        key={item.id}
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderColor: selected ? 'primary.main' : 'divider',
                                            boxShadow: 'none',
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'stretch', sm: 'center' }}
                                            gap={1.5}
                                        >
                                            <Box>
                                                <Typography fontWeight={600}>{item.title}</Typography>
                                                <Typography variant="body2">
                                                    {item.credited_dialogs} диалогов · {rubles(price.total)}
                                                </Typography>
                                                <Typography variant="caption" color="secondary.main">
                                                    {price.effective
                                                        ? `${rubles(price.effective)} за диалог`
                                                        : 'Цена за диалог недоступна'}
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant={selected ? 'contained' : 'outlined'}
                                                onClick={() => setSelectedPackageId(item.id)}
                                                aria-pressed={selected}
                                                disabled={working || !price.effective}
                                            >
                                                {selected ? 'Выбрано' : 'Выбрать'}
                                            </Button>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    ) : (
                        !notice && <Alert severity="info">Пакеты пока недоступны.</Alert>
                    )}
                    {pending?.purchaseId && pending.status === 'pending' && (
                        <Button variant="outlined" onClick={() => void reconcile(pending)} disabled={working}>
                            Проверить оплату
                        </Button>
                    )}
                    {pending?.status === 'canceled' && (
                        <Button variant="outlined" onClick={startNewPurchase} disabled={working}>
                            Начать новую покупку
                        </Button>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={working}>
                    Закрыть
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void purchase()}
                    disabled={working || !selectedPackageId || pending?.status === 'canceled'}
                >
                    {working ? 'Проверяем…' : 'Перейти к оплате'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
